import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BaseHttpService, IBaseResponse } from 'O3_core';
import {
  ITicketAttachment,
  ITicketAttachmentDownload,
} from '../models/ticket.interface';

/**
 * What the client did to an image before uploading it.
 *
 * Travels with the file so the console can show how an image arrived. It is a
 * record of an assertion, not a proof — the server receives a flat PNG either
 * way and has no means of checking it.
 */
export interface IRedactionRecord {
  applied: boolean;
  count: number;
}

/** PNG only, 5 MB — mirrors the server-side cap so the UI can fail fast. */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACHMENT_ALLOWED_TYPES = ['image/png'];

/**
 * Multipart uploads go through `BaseHttp` like everything else.
 *
 * An earlier version reached for `HttpClient` directly, on the theory that
 * `BaseHttp` would impose a JSON content type and destroy the multipart
 * boundary. It does not: `BaseHttp.post` hands the body to `HttpClient`
 * untouched, and Angular deliberately sets no `Content-Type` for a `FormData`
 * body so the browser can supply one with the boundary.
 *
 * The bypass cost real behaviour. `BaseHttp.handleError` is what converts an
 * `HttpErrorResponse` into the `{ resultCode, message }` shape that
 * `ErrorManagerService.processError` reads off the top level; without it the
 * server's `ENTITY_NOT_FOUND` / validation codes stayed buried under `.error`
 * and every attachment failure surfaced as "An error occurred".
 */
@Injectable({ providedIn: 'root' })
export class TicketAttachmentService extends BaseHttpService<ITicketAttachment> {
  constructor() {
    super({ service: 'support', feature: 'ticket-attachment', version: 'v1' });
  }

  /**
   * Console upload — scope-gated, any ticket in the tenant.
   *
   * Paired with {@link uploadToMyTicket} rather than switched on a flag: the
   * route is the authorisation boundary server-side, so a caller cannot reach
   * the privileged variant by accident.
   */
  upload(
    file: File,
    ticketId: string,
    ticketCommentId?: string,
    redaction?: IRedactionRecord,
  ): Observable<ITicketAttachment> {
    return this.postUpload(
      `${this.apiUrl}/upload`,
      file,
      ticketId,
      ticketCommentId,
      redaction,
    );
  }

  /** Reporter upload — the caller must have reported the ticket. */
  uploadToMyTicket(
    file: File,
    ticketId: string,
    ticketCommentId?: string,
    redaction?: IRedactionRecord,
  ): Observable<ITicketAttachment> {
    return this.postUpload(
      `${this.apiUrl}/my/upload`,
      file,
      ticketId,
      ticketCommentId,
      redaction,
    );
  }

  /** Console download. Returns a short-lived presigned URL; audit-logged. */
  getDownloadUrl(id: string): Observable<ITicketAttachmentDownload> {
    return this.get<IBaseResponse<ITicketAttachmentDownload>>(
      `${this.apiUrl}/${id}/download`,
    ).pipe(map((response) => response.data));
  }

  /** Reporter download, restricted to their own ticket. Also audit-logged. */
  getMyDownloadUrl(id: string): Observable<ITicketAttachmentDownload> {
    return this.get<IBaseResponse<ITicketAttachmentDownload>>(
      `${this.apiUrl}/my/${id}/download`,
    ).pipe(map((response) => response.data));
  }

  private postUpload(
    url: string,
    file: File,
    ticketId: string,
    ticketCommentId?: string,
    redaction?: IRedactionRecord,
  ): Observable<ITicketAttachment> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('ticketId', ticketId);
    if (ticketCommentId) form.append('ticketCommentId', ticketCommentId);

    // Sent as text because a multipart body has no other kind of field. The
    // server records these; it cannot and does not verify them.
    if (redaction) {
      form.append('redactionApplied', String(redaction.applied));
      form.append('redactionCount', String(redaction.count));
    }

    // No headers argument: Angular omits `Content-Type` for a `FormData` body
    // so the browser can set it together with the multipart boundary.
    return this.post<IBaseResponse<ITicketAttachment>>(url, form).pipe(
      map((response) => response.data),
    );
  }

  /** `null` when acceptable, otherwise a translation key describing why not. */
  static validate(file: File): string | null {
    if (!ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
      return 'attachmentTypeError';
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      return 'attachmentSizeError';
    }
    return null;
  }
}
